import React, { memo } from "react";
import { Stack, Chip } from "investira.react.components";

// Função utilitária para extrair valores únicos de um campo do array de dados
const extrairOpcoesUnicas = (pData, pCampo) => {
  if (!Array.isArray(pData)) return [];
  let valores = [];
  if (pCampo === "pessoa_tipo") {
    // pessoa_tipo é um array, então precisamos achatar todos os arrays e pegar os valores únicos
    pData.forEach((item) => {
      if (Array.isArray(item[pCampo])) {
        valores = valores.concat(item[pCampo]);
      } else if (item[pCampo] !== undefined && item[pCampo] !== null) {
        valores.push(item[pCampo]);
      }
    });
  } else {
    valores = pData
      .map((item) => item[pCampo])
      .filter((v) => v !== undefined && v !== null);
  }
  // Remover duplicatas
  return Array.from(new Set(valores));
};

// Mapeamento dos níveis e seus campos correspondentes
const niveis = {
  pessoa: [
    { campo: "pessoa_tipo", label: "Tipo de Empresa" },
    { campo: "top_setor", label: "Top Setor" },
    { campo: "setor", label: "Setor" },
  ],
  ativo: [
    { campo: "mercado_descricao", label: "Mercado" },
    { campo: "ativo_tipo", label: "Tipo de Ativo" },
    { campo: "top_setor", label: "Top Setor" },
    // { campo: "setor", label: "Setor" },
  ],
  indicador: [],
};

/**
 * Renderiza o componente SearchGlobalFilter.
 *
 * @param {Object} props - Propriedades do componente.
 * @param {*} props.data - Propriedade `data` do componente.
 * @param {*} props.filter - Propriedade `filter` do componente.
 * @param {*} props.setFilter - Propriedade `setFilter` do componente.
 *
 * @description
 * Este componente realiza as seguintes operacoes:
 * 1. Recebe as propriedades necessarias para compor a interface
 * 2. Organiza os estados e callbacks usados durante a renderizacao
 * 3. Renderiza a experiencia visual do organismo
 *
 * @returns {JSX.Element|null} Elemento renderizado pelo componente.
 */

const SearchGlobalFilter = memo((props) => {
  const { data, filter, setFilter } = props;

  // Cria dinamicamente o objeto root com base nos dados disponíveis em 'data'
  const root = React.useMemo(() => {
    // Verifica se há dados de cada tipo
    const temPessoa =
      Array.isArray(data) &&
      data.some((item) => item.pesquisa_origem === "pessoa");
    const temAtivo =
      Array.isArray(data) &&
      data.some((item) => item.pesquisa_origem === "ativo");
    const temIndicador =
      Array.isArray(data) &&
      data.some((item) => item.pesquisa_origem === "indicador");

    const obj = {};

    if (temPessoa) {
      obj.pessoa = {
        id: "pessoa",
        descricao: "Empresa",
        value: { pesquisa_origem: "pessoa" },
      };
    }
    if (temAtivo) {
      obj.ativo = {
        id: "ativo",
        descricao: "Ativo",
        value: { pesquisa_origem: "ativo" },
      };
    }
    if (temIndicador) {
      obj.indicador = {
        id: "indicador",
        descricao: "Indicador",
        value: { pesquisa_origem: "indicador" },
      };
    }

    // Sempre adiciona a opção "Todos" no início
    return {
      todos: {
        id: "todos",
        descricao: "Todos",
        value: { pesquisa_origem: null },
      },
      ...obj,
    };
  }, [data]);

  // Determina o tipo de pesquisa_origem selecionado
  const pesquisaOrigem = filter?.pesquisa_origem;

  // Função para desfazer o filtro de um nível específico
  const desfazerNivel = (pNivel) => {
    // Remove todos os filtros a partir do nível informado
    if (!pesquisaOrigem) {
      setFilter({ pesquisa_origem: null });
      return;
    }
    const campos = niveis[pesquisaOrigem] || [];
    const novoFiltro = { ...filter };
    for (let i = pNivel; i < campos.length; i++) {
      delete novoFiltro[campos[i].campo];
    }
    setFilter(novoFiltro);
  };

  // Função para resetar o filtro do root (pesquisa_origem) para "Todos"
  const resetarRootParaTodos = () => {
    setFilter({ pesquisa_origem: null });
  };

  // Função para atualizar o filtro ao selecionar uma opção em um nível
  const selecionarOpcao = (pCampo, pValor) => {
    setFilter({
      ...filter,
      [pCampo]: pValor === "todos" ? undefined : pValor,
    });
  };

  // Determina o nível atual de filtro
  let nivelAtual = 0;
  if (pesquisaOrigem && niveis[pesquisaOrigem]) {
    const campos = niveis[pesquisaOrigem];
    for (let i = 0; i < campos.length; i++) {
      if (
        filter[campos[i].campo] === undefined ||
        filter[campos[i].campo] === null
      ) {
        nivelAtual = i;
        break;
      }
      nivelAtual = i + 1;
    }
  }

  // Filtra os dados para cada nível, reduzindo conforme os filtros já aplicados
  let dadosFiltrados = Array.isArray(data) ? data : [];
  if (pesquisaOrigem) {
    dadosFiltrados = dadosFiltrados.filter(
      (item) => item.pesquisa_origem === pesquisaOrigem
    );
    const campos = niveis[pesquisaOrigem] || [];
    for (let i = 0; i < nivelAtual; i++) {
      const campo = campos[i].campo;
      if (filter[campo] !== undefined && filter[campo] !== null) {
        if (campo === "pessoa_tipo") {
          // pessoa_tipo é array, então precisa verificar se o valor está incluso no array
          dadosFiltrados = dadosFiltrados.filter(
            (item) =>
              Array.isArray(item[campo]) && item[campo].includes(filter[campo])
          );
        } else {
          dadosFiltrados = dadosFiltrados.filter(
            (item) => item[campo] === filter[campo]
          );
        }
      }
    }
  }

  // Lógica para o nível root (pesquisa_origem)
  // Se uma opção diferente de "Todos" está selecionada, mostra apenas o chip selecionado com X para resetar para "Todos"
  // Se "Todos" está selecionado (pesquisa_origem === null), mostra todos os chips do root, com "Todos" destacado

  return (
    <Stack direction="row" spacing={1} sx={{ px: 2, py: 1 }}>
      {/* Nível root: pesquisa_origem */}
      <Stack direction="row" spacing={1}>
        {pesquisaOrigem !== null && pesquisaOrigem !== undefined
          ? // Exibe apenas o chip selecionado com X para resetar para "Todos"
            (() => {
              const keySelecionada = Object.keys(root).find(
                (key) => root[key].value.pesquisa_origem === pesquisaOrigem
              );
              if (!keySelecionada) return null;
              return (
                <Chip
                  label={root[keySelecionada].descricao}
                  color="primary"
                  onDelete={resetarRootParaTodos}
                  size="small"
                  sx={{ textTransform: "capitalize" }}
                />
              );
            })()
          : // Exibe todos os chips do root, com "Todos" destacado se for o caso
            Object.keys(root).map((key) => (
              <Chip
                key={key}
                label={root[key].descricao}
                color={
                  (pesquisaOrigem === null &&
                    root[key].value.pesquisa_origem === null) ||
                  (pesquisaOrigem === undefined &&
                    root[key].value.pesquisa_origem === null)
                    ? "primary"
                    : pesquisaOrigem === root[key].value.pesquisa_origem
                    ? "primary"
                    : "default"
                }
                onClick={() => {
                  if (root[key].value.pesquisa_origem === null) {
                    setFilter({ pesquisa_origem: null });
                  } else {
                    setFilter(root[key].value);
                  }
                }}
                clickable
                size={"small"}
                sx={{ textTransform: "capitalize" }}
              />
            ))}
      </Stack>

      {/* Níveis seguintes */}
      {pesquisaOrigem &&
        (niveis[pesquisaOrigem] || []).map((nivel, idx) => {
          // Só exibe o nível se o anterior foi selecionado
          if (idx > nivelAtual) return null;
          // Se este nível já foi selecionado, mostra o chip selecionado com opção de desfazer
          if (
            filter[nivel.campo] !== undefined &&
            filter[nivel.campo] !== null
          ) {
            return (
              <Stack direction="row" spacing={1} key={nivel.campo}>
                <Chip
                  label={
                    filter[nivel.campo] === "" ||
                    filter[nivel.campo] === undefined
                      ? "Todos"
                      : filter[nivel.campo]
                  }
                  color="primary"
                  onDelete={() => desfazerNivel(idx)}
                  size="small"
                  sx={{ textTransform: "capitalize" }}
                />
              </Stack>
            );
          }
          // Se este é o próximo nível a ser selecionado, mostra as opções
          if (idx === nivelAtual) {
            // Extrai opções únicas deste campo a partir dos dados filtrados
            const opcoes = extrairOpcoesUnicas(dadosFiltrados, nivel.campo);
            // Sempre inclui a opção "Todos"
            return (
              <Stack
                direction="row"
                spacing={1}
                key={nivel.campo}
                sx={{
                  overflowX: "auto",
                  flexWrap: "nowrap",
                  // Oculta a barra de rolagem horizontal
                  scrollbarWidth: "none",
                  "&::-webkit-scrollbar": {
                    display: "none",
                  },
                }}
              >
                <Chip
                  label="Todos"
                  color={
                    filter[nivel.campo] === undefined ||
                    filter[nivel.campo] === null ||
                    filter[nivel.campo] === ""
                      ? "primary"
                      : "default"
                  }
                  onClick={() => selecionarOpcao(nivel.campo, "todos")}
                  clickable
                  size="small"
                  sx={{ textTransform: "capitalize" }}
                />
                {opcoes.map((opcao) => (
                  <Chip
                    key={opcao}
                    label={opcao}
                    color={
                      filter[nivel.campo] === opcao ? "primary" : "default"
                    }
                    onClick={() => selecionarOpcao(nivel.campo, opcao)}
                    clickable
                    size="small"
                    sx={{ textTransform: "capitalize" }}
                  />
                ))}
              </Stack>
            );
          }
          return null;
        })}
    </Stack>
  );
});

export default SearchGlobalFilter;
